import { ChangeDetectionStrategy, Component, EventEmitter, HostBinding, Input, OnChanges, Output } from '@angular/core';
import { SafeResourceUrl } from '@angular/platform-browser';

import { Breakpoint, CssUtils } from '@hdms/shared/util-core';

interface SourceSet {
  src: string | SafeResourceUrl;
  media: string;
}

@Component({
  selector: 'shrd-uic-image',
  templateUrl: './image.component.html',
  styleUrls: ['./image.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ImageComponent implements OnChanges {
  static readonly placeholderImageUrl = '/assets/images/noimage_placeholder_full.png';
  static readonly substituteSrc = 'data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==';

  @Input() src: string | SafeResourceUrl;
  @Input() srcSm?: string;
  @Input() srcMd?: string;
  @Input() srcLg?: string;
  @Input() srcXl?: string;
  @Input() alt = '';
  @Input() title?: string;
  @Input() containerClass?: string;
  @Input() maxHeight?: string | number;
  @Input() maxWidth?: string | number;
  @Input() alignX?: 'left' | 'right';
  @Input() alignY?: 'top' | 'bottom';
  @Input() usePlaceholder?: boolean;
  @Input() noUpScale?: boolean;

  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() error: EventEmitter<Event> = new EventEmitter<Event>();

  public sources: SourceSet[] = [];

  @HostBinding('class.shrd-uic-image') readonly hostClass = true;
  @HostBinding('class.shrd-uic-image-left') get isLeftAligned() {
    return this.alignX === 'left';
  }
  @HostBinding('class.shrd-uic-image-right') get isRightAligned() {
    return this.alignX === 'right';
  }
  @HostBinding('class.shrd-uic-image-top') get isTopAligned() {
    return this.alignY === 'top';
  }
  @HostBinding('class.shrd-uic-image-bottom') get isBottomAligned() {
    return this.alignY === 'bottom';
  }
  @HostBinding('class.shrd-uic-image-no-upscale') get isNoUpScale() {
    return this.noUpScale || (this.maxHeight && !this.maxWidth);
  }

  @HostBinding('class.shrd-uic-image-max-height-scale') get isMaxHeightScale() {
    return !this.noUpScale && this.maxHeight && !this.maxWidth;
  }

  ngOnChanges() {
    if (!this.src && this.usePlaceholder) {
      this.src = ImageComponent.placeholderImageUrl;
    }

    this.sources = this.src ? this.generateMqSources() : [];
  }

  onImageError(event: Event) {
    const imgTarget = event.currentTarget as HTMLImageElement;
    const errorImg = imgTarget.currentSrc || imgTarget.src;
    const formats = ['src', 'srcSm', 'srcMd', 'srcLg', 'srcXl'];

    // filter out the erroneous image also from the srcset
    formats.forEach(key => {
      // src could be SafeResourceUrl so let's also check `changingThisBreaksApplicationSecurity` prop
      if (errorImg.includes(this[key]?.changingThisBreaksApplicationSecurity || this[key])) {
        this[key] = null;
      }
    });

    if (!formats.some(key => this[key]) && !this.usePlaceholder) {
      this.error.emit(event);
    }

    if (!this.src) {
      this.src =
        this.usePlaceholder && !errorImg.includes(ImageComponent.placeholderImageUrl)
          ? ImageComponent.placeholderImageUrl
          : ImageComponent.substituteSrc;
    }

    this.sources = this.generateMqSources();
  }

  private generateMqSources(): SourceSet[] {
    const mqSrcMap = {
      [Breakpoint.XS]: this.src,
      [Breakpoint.SM]: this.srcSm,
      [Breakpoint.MD]: this.srcMd,
      [Breakpoint.LG]: this.srcLg,
      [Breakpoint.XL]: this.srcXl
    };

    return Object.keys(mqSrcMap)
      .filter(key => mqSrcMap[key])
      .map((key: Breakpoint) => ({
        src: mqSrcMap[key],
        media: CssUtils.getMediaQuery(key)
      }));
  }
}
